import React, { useEffect, useRef } from 'react';
import { Mic, Plus, Send, Square } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useChat } from '../../hooks/useChat';

const InputArea = () => {
  const { theme, loading, inputDraft, setInputDraft } = useAppContext();
  const { handleQuerySubmit, stopGeneration } = useChat();
  const canSend = inputDraft.trim().length > 0;
  const textareaRef = useRef(null);
  const maxTextareaHeight = 160;

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const nextHeight = Math.min(textarea.scrollHeight, maxTextareaHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxTextareaHeight ? 'auto' : 'hidden';
  }, [inputDraft]);

  const submitMessage = () => {
    if (!inputDraft.trim() || loading) return;
    handleQuerySubmit(inputDraft);
    setInputDraft('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitMessage();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitMessage();
    }
  };

  return (
    <div className="pt-6 pb-6 max-w-4xl mx-auto w-full">
      <form
        onSubmit={handleSubmit}
        className={`relative flex items-center gap-3 rounded-full px-3 py-2 border shadow-sm transition-colors
          ${theme === 'dark'
            ? 'bg-gray-900/70 border-gray-700'
            : 'bg-white border-gray-200'
          }`}
      >
        <button
          type="button"
          aria-label="Add attachment"
          disabled
          className={`h-10 w-10 flex items-center justify-center rounded-full border transition-colors cursor-not-allowed opacity-50
            ${theme === 'dark'
              ? 'border-gray-700 text-gray-500'
              : 'border-gray-200 text-gray-400'
            }`}
        >
          <Plus size={18} />
        </button>

        <textarea
          type="text"
          ref={textareaRef}
          value={inputDraft}
          onChange={(e) => setInputDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask anything"
          rows={1}
          className={`flex-grow bg-transparent outline-none px-2 text-base resize-none
            ${theme === 'dark'
              ? 'text-gray-200 placeholder-gray-400'
              : 'text-gray-800 placeholder-gray-500'
            }`}
        />

        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Voice input"
            disabled
            className={`h-10 w-10 flex items-center justify-center rounded-full border transition-colors cursor-not-allowed opacity-50
              ${theme === 'dark'
                ? 'border-gray-700 text-gray-500'
                : 'border-gray-200 text-gray-400'
              }`}
          >
            <Mic size={18} />
          </button>

          {loading ? (
            <button
              type="button"
              onClick={stopGeneration}
              aria-label="Stop generating"
              className="h-10 w-10 flex items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              <Square size={18} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!canSend}
              aria-label="Send message"
              className={`h-10 w-10 flex items-center justify-center rounded-full transition-colors
                ${canSend
                  ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                  : theme === 'dark'
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
            >
              <Send size={18} />
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default InputArea;
